import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import ListArticleListItem from '@/features/editorial/components/article/list/ListArticleListItem.vue';
import ListArticleFilter from '@/features/editorial/components/article/list/ListArticleFilter.vue';

import type { TitleEdge } from '@/features/editorial/types/editorial-types';
import { TitleObjectType } from '@/interfaces/titles';
import { TitleOrder } from '@/enums/title-order';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useFollowList } from '@/helpers/composables/useFollowList';
import { addToast } from '@/helpers/vfm/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useI18n } from '@/helpers/composables/useI18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'ListArticleList',
  props: {
    titles: null,
    titleOrderMode: { default: 'unranked' },
    listId: null,
    listName: null,
    isFollowed: { type: Boolean },
    listSize: null
  },
  setup(__props: any) {

const props = __props as { titles: TitleEdge[], titleOrderMode: string, listId: string, listName: string, isFollowed: boolean, listSize: number };



const filterStore = useFilterStore();

const { tc } = useI18n();

const activeContentType = ref<TitleObjectType | null>(null);

const hasMixedObjectTypes = computed(() => {
	const types = new Set(props.titles.map(t => t.node.objectType));
	return types.size > 1;
});

function toggleContentType(value: TitleObjectType) {
	activeContentType.value = activeContentType.value === value ? null : value;
}

const { toggleFollowList } = useFollowList();

function onAddTitles() {
	toggleFollowList({
		listId: props.listId,
		isFollowed: props.isFollowed,
		listName: props.listName,
	});
}

const isFilterActive = computed(
	() => activeContentType.value !== null || (filterStore.currentFilters?.providers?.length ?? 0) > 0
);

const filteredTitles = computed(() => {
	let result = props.titles;

	if (activeContentType.value !== null) {
		result = result.filter(t => t.node.objectType.toLowerCase() === activeContentType.value);
	}

	const activeProviders = filterStore.currentFilters?.providers ?? [];
	if (activeProviders.length > 0) {
		result = result.filter(t => t.node.offers?.some(o => activeProviders.includes(o.package.shortName)));
	}

	const sortBy = filterStore.currentFilters?.sort_by ?? null;
	return sortTitles(result, sortBy);
});

function sortTitles(titles: TitleEdge[], sortBy: TitleOrder | null): TitleEdge[] {
	if (!sortBy || sortBy === TitleOrder.POPULARITY) return titles;

	switch (sortBy) {
		case TitleOrder.ALPHABETICAL:
			return titles.toSorted((a, b) => a.node.content.title.localeCompare(b.node.content.title));
		case TitleOrder.RELEASE_YEAR:
			return titles.toSorted(
				(a, b) => (b.node.content.originalReleaseYear ?? 0) - (a.node.content.originalReleaseYear ?? 0)
			);
		case TitleOrder.SCORE_IMDB:
			return titles.toSorted(
				(a, b) => (b.node.content.scoring?.imdbScore ?? 0) - (a.node.content.scoring?.imdbScore ?? 0)
			);
		case TitleOrder.TMDB_POPULARITY:
			return titles.toSorted(
				(a, b) => (b.node.content.scoring?.tmdbPopularity ?? 0) - (a.node.content.scoring?.tmdbPopularity ?? 0)
			);
		default:
			return titles;
	}
}

function onShareList() {
	if (navigator && navigator.clipboard) {
		navigator.clipboard.writeText(window.location.href);
		addToast({ message: tc('WEBAPP_COPIED_TO_CLIPBOARD') });

		TrackingHelper.trackEvent('userinteraction', {
			action: 'share_content',
			label: 'list',
			property: props.listId,
		});
	}
}

return { __sfc: true,props, filterStore, tc, activeContentType, hasMixedObjectTypes, toggleContentType, toggleFollowList, onAddTitles, isFilterActive, filteredTitles, sortTitles, onShareList, ListArticleListItem, ListArticleFilter }
}

})